import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent } from '../types.js';
import {
  bilingual,
  description,
  hasTitle,
  image,
  languageFactor,
  price,
  sectionSubtitle,
  sectionTitle,
  sellingPrice,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * A chalkboard price list: brand and title centred at the top between two pictures, sections
 * in columns under brush-stroke headings, and dotted leaders from each name to its price.
 */

const DECORATIONS = [
  'Picture left of the title',
  'Picture right of the title',
  'Bottom-left corner',
  'Bottom-right corner',
];

// Fine noise over a soft vignette reads as chalk dust on slate.
const CHALK =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 .07 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")";

function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const lf = languageFactor(board);
  const desc = board.settings.showDescriptions ? 1.4 : 0;
  const column: MenuColumn = {
    capacity: portrait ? 88 : 44,
    blockCost: (section, items, continued) =>
      (hasTitle(section) ? 3.8 * lf : 0) +
      (!continued && (section.subtitle || section.subtitleAr) ? 1.5 : 0) +
      items.reduce((sum, item) => sum + (2.3 + (item.description || item.descriptionAr ? desc : 0)) * lf + 1, 0) +
      1.6,
  };
  return Array.from({ length: portrait ? 2 : 3 }, () => column);
}

function itemPrice(ctx: RenderContext, item: MenuBlock['items'][number]): string {
  if (!item.variants.length) return sellingPrice(ctx, item, 'cb-price');
  return item.variants
    .map((v) => `<span class="cb-price"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <span dir="ltr">${price(ctx, v.price)}</span></span>`)
    .join('<span class="cb-slash">/</span>');
}

function lineName(ctx: RenderContext, item: MenuBlock['items'][number]): string {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  return arabicFirst
    ? `<span class="cb-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</span>`
    : `<span class="cb-name">${escapeHtml(item.title)}</span>`;
}

function secondName(ctx: RenderContext, item: MenuBlock['items'][number]): string {
  if (!(ctx.showEn && ctx.showAr && item.titleAr?.trim())) return '';
  return `<p class="cb-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`;
}

function block(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section) ? `<div class="cb-ribbon">${sectionTitle(ctx, b.section, 'cb-rt')}</div>` : '';
  const note = b.continued ? '' : sectionSubtitle(ctx, b.section, 'cb-note');
  const rows = b.items
    .map((item) =>
      [
        '<div class="cb-row">',
        // The leader runs from the first name to the price; a second language sits underneath.
        `<div class="cb-line">${lineName(ctx, item)}<span class="cb-dots"></span>${itemPrice(ctx, item)}</div>`,
        secondName(ctx, item),
        description(ctx, item, 'cb-desc'),
        '</div>',
      ].join(''),
    )
    .join('');
  return `<div class="cb-sec">${head}${note}${rows}</div>`;
}

export const chalkboard: MenuTemplate = {
  id: 'chalkboard',
  name: 'Chalkboard',
  description:
    'A dark chalkboard price list in columns, with brush-stroke headings, dotted price lines and pictures around the title.',
  defaultTheme: {
    backgroundColor: '#161616',
    surfaceColor: '#232323',
    textColor: '#f3f3f3',
    primaryColor: '#ffffff',
    accentColor: '#f5c431',
    fontId: 'chalk',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'slate', name: 'Slate & yellow', theme: {} },
    { id: 'green', name: 'Green board', theme: { backgroundColor: '#1d3226', surfaceColor: '#244030' } },
    { id: 'white', name: 'White chalk', theme: { accentColor: '#f4f1ea', primaryColor: '#ffe8a3' } },
    { id: 'red', name: 'Red chalk', theme: { accentColor: '#ff6b5e' } },
  ],
  cuisines: ['burgers'],
  uses: { featured: false, sectionImages: false, brand: true, decorations: DECORATIONS },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const { board } = ctx;
    const decor = board.decorMediaIds.map((id, i) => image(ctx, id, `cb-decor cb-decor-${i}`)).join('');
    const header = [
      '<div class="cb-header">',
      bilingual(ctx, board.brand.name, board.brand.nameAr, 'cb-brand'),
      bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'cb-title'),
      bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'cb-tagline'),
      '</div>',
    ].join('');
    const cols = ctx.page.columns
      .map((column) => `<div class="cb-col">${column.map((b) => block(ctx, b)).join('')}</div>`)
      .join('');
    return `${decor}${header}<div class="cb-cols">${cols}</div>`;
  },

  css: `
.mb-t-chalkboard{padding:2em 3.5em 2.5em;
  background-image:${CHALK},radial-gradient(ellipse at 50% 40%,rgba(255,255,255,.07),transparent 70%)}
.mb-t-chalkboard .cb-decor{position:absolute;z-index:0;object-fit:cover;border:.4em solid #f4f1ea;border-radius:.3em;
  box-shadow:0 1em 2em rgba(0,0,0,.6)}
.mb-t-chalkboard .cb-decor-0{top:1em;left:5%;width:17em;height:11em;transform:rotate(-5deg)}
.mb-t-chalkboard .cb-decor-1{top:1em;right:5%;width:17em;height:11em;transform:rotate(5deg)}
.mb-t-chalkboard .cb-decor-2{bottom:1.6em;left:2.5%;width:14em;height:9.5em;transform:rotate(4deg)}
.mb-t-chalkboard .cb-decor-3{bottom:1.6em;right:2.5%;width:14em;height:9.5em;transform:rotate(-4deg)}
.mb-t-chalkboard.mb-portrait .cb-decor-0,.mb-t-chalkboard.mb-portrait .cb-decor-1{width:14em;height:10em}
.mb-t-chalkboard.mb-portrait .cb-decor-2,.mb-t-chalkboard.mb-portrait .cb-decor-3{width:12em;height:8em}
.mb-t-chalkboard .cb-header{position:relative;z-index:1;text-align:center;margin:0 auto 1.6em;max-width:56%}
.mb-t-chalkboard.mb-portrait .cb-header{max-width:46%;margin-bottom:2.4em}
.mb-t-chalkboard .cb-brand{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:2.6em;line-height:1.1}
.mb-t-chalkboard .cb-brand.mb-2nd{font-size:1.8em}
.mb-t-chalkboard .cb-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:6em;line-height:.95;
  text-transform:uppercase;color:var(--mb-primary);text-shadow:0 0 .08em rgba(255,255,255,.25)}
.mb-t-chalkboard .cb-title.mb-2nd{font-size:3.4em}
.mb-t-chalkboard.mb-portrait .cb-title{font-size:4.6em}
.mb-t-chalkboard .cb-tagline{font-size:1em;letter-spacing:.22em;text-transform:uppercase;font-weight:600;opacity:.85;margin-top:.6em}
.mb-t-chalkboard .cb-tagline.mb-2nd{letter-spacing:0;margin-top:.2em}
.mb-t-chalkboard .cb-cols{position:relative;z-index:1;flex:1;min-height:0;display:grid;grid-template-columns:repeat(3,1fr);gap:3.5em}
.mb-t-chalkboard.mb-portrait .cb-cols{grid-template-columns:1fr 1fr;gap:3em}
.mb-t-chalkboard .cb-col{min-width:0;display:flex;flex-direction:column;gap:1.6em}
.mb-t-chalkboard .cb-ribbon{align-self:flex-start;min-width:55%;background:var(--mb-accent);color:var(--mb-bg);padding:.35em 1.4em .3em;
  transform:rotate(-1.2deg);margin-bottom:.9em;
  clip-path:polygon(2% 10%,14% 0,31% 7%,52% 1%,71% 8%,90% 0,100% 14%,98% 52%,100% 86%,87% 100%,65% 93%,45% 100%,24% 92%,7% 100%,0 84%,2% 50%)}
.mb-t-chalkboard .cb-rt{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.9em;
  text-transform:uppercase;line-height:1.05}
.mb-t-chalkboard .cb-rt.mb-2nd{font-size:1.4em}
.mb-t-chalkboard .cb-note{font-size:.85em;font-style:italic;color:var(--mb-accent);margin:-.3em 0 .8em}
.mb-t-chalkboard .cb-sec{display:flex;flex-direction:column}
.mb-t-chalkboard .cb-row{margin-bottom:1em}
.mb-t-chalkboard .cb-line{display:flex;align-items:baseline;gap:.5em}
.mb-t-chalkboard .cb-name{font-weight:800;font-size:1.15em;text-transform:uppercase;color:var(--mb-accent);letter-spacing:.02em;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:70%}
.mb-t-chalkboard .cb-name2{font-weight:700;font-size:1em;color:var(--mb-accent);opacity:.9;margin-top:.1em}
.mb-t-chalkboard .cb-dots{flex:1;min-width:1.5em;border-bottom:.16em dotted rgba(255,255,255,.35);transform:translateY(-.3em)}
.mb-t-chalkboard .cb-price{font-weight:800;font-size:1.15em;color:var(--mb-accent);white-space:nowrap}
.mb-t-chalkboard .cb-price small{font-size:.65em;opacity:.8}
.mb-t-chalkboard .cb-price-old{font-size:.85em;opacity:.55;white-space:nowrap}
.mb-t-chalkboard .cb-slash{opacity:.5}
.mb-t-chalkboard .cb-desc{font-size:.85em;opacity:.85;margin-top:.2em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
`,
};

